<template>
  <div class="content">

    <div v-if="step < 4" class="d-flex justify-content-between actions-down">
      <label class="location-view-toggle" for="archival-location-view-toggle" v-if="step === 1">
        <span class="toggle-label">{{ t('map').toUpperCase() }}</span>
        <input id="archival-location-view-toggle" type="checkbox" v-model="useMapLocationList" />
        <span class="toggle-slider"></span>
      </label>
      <button class="btn-prev" v-if="step > 1" @click="previousStep()">
        {{ t('back2').toUpperCase() }}
      </button>

      <button
          class="btn-next ms-auto"
          v-if="step < 4"
          @click="nextStep()"
          :disabled="!canGoNext"
      >
        {{ step < 3 ? t('next').toUpperCase() : t('apply').toUpperCase() }}
      </button>
    </div>

    <div
      v-show="!hideFilters"
      class="archival-filters"
      :id="`archival-filters`"
    >
      <!-- STEP 1 -->
      <div v-if="step === 1">
        <div class="d-flex step-content step1">
          <component :is="locationListComponent" :store="filtersStore" cssClass="archival" />
          <date-range :store="filtersStore" />
        </div>
      </div>

      <!-- STEP 2 -->
      <div v-else-if="step === 2 && !isSG" class="d-flex step-content step2 flex-wrap">
        <violation-list :store="filtersStore" />
        <vehicle-categories :store="filtersStore" :step="step" />
        <lane-numbers :store="filtersStore" :step="step" />
        <div class="break"></div>
        <archival-vehicle-data />
      </div>

      <div v-else-if="step === 2 && isSG" class="d-flex step-content step2 flex-wrap">
        <archival-vehicle-data />
      </div>

      <!-- STEP 3 -->
      <div v-else-if="step === 3" class="step-content step3 sort-panel">
        <div class="sort-panel__row" v-for="n in (isSG ? 2 : 3)" :key="n">
          <select-filter
              class="sort-panel__control"
              :name="t('type')"
              :fieldName="`orderByColumnName${n}`"
              type="column"
              :store="filtersStore"
              :step="step"
              :allow-empty="n > 1"
          />
          <select-filter
              class="sort-panel__control"
              :name="t('direction')"
              :fieldName="`orderDirection${n}`"
              type="direction"
              :store="filtersStore"
              :step="step"
          />
        </div>
      </div>
    </div>

    <!-- STEP 4 -->
    <div v-if="step === 4" class="content">
      <h3 class="d-flex">
        <div class="d-flex justify-content-between actions btn-top me-auto">
          <!-- Przycisk powrotu do filtrów -->
          <button class="btn-prev-top" @click="previousStep()">{{ t('back2').toUpperCase() }}</button>
        </div>
        <div class="d-flex total-count">
          <!-- Liczba wyników -->
          <span>
            Ilość wyników: {{ totalCount }}
          </span>
        </div>

        <!-- Eksport PDF -->
        <div class="dropdown-wrapper" @click="toggleDropdown('pdf')">
<!--          <button class="btn" :disabled="noData">-->
          <button class="btn">
            <span>PDF</span>
          </button>
          <ul v-show="isOpenPdf" class="dropdown-list">
            <li @click.stop="selectOption('pdf')">
              <span v-show="isDownloadingPdf" class="spinner-border spinner-border-sm me-1"></span>
              PDF
            </li>
            <li @click.stop="selectOption('pdf-with-image')">
              <span v-show="isDownloadingPdfWithImage" class="spinner-border spinner-border-sm me-1"></span>
              PDF {{ t('with-image') }}
            </li>
          </ul>
        </div>

        <!-- Eksport CSV -->
        <div class="dropdown-wrapper" @click="toggleDropdown('csv')">
<!--          <button class="btn" :disabled="noData">-->
          <button class="btn">
            <span>CSV</span>
          </button>
          <ul v-show="isOpenCsv" class="dropdown-list">
            <li @click.stop="selectOption('csv')">
              <span v-show="isDownloadingCsv" class="spinner-border spinner-border-sm me-1"></span>
              CSV
            </li>
            <li @click.stop="selectOption('csv-with-image')">
              <span v-show="isDownloadingCsvWithImage" class="spinner-border spinner-border-sm me-1"></span>
              CSV {{ t('with-image') }}
            </li>
          </ul>
        </div>

        <!-- Eksport XLSX -->
        <div class="dropdown-wrapper" @click="toggleDropdown('xlsx')">
<!--          <button class="btn" :disabled="noData">-->
          <button class="btn">
            <span>XLSX</span>
          </button>
          <ul v-show="isOpenXlsx" class="dropdown-list">
            <li @click.stop="selectOption('xlsx')">
              <span v-show="isDownloadingXlsx" class="spinner-border spinner-border-sm me-1"></span>
              XLSX
            </li>
            <li @click.stop="selectOption('xlsx-with-image')">
              <span v-show="isDownloadingXlsxWithImage" class="spinner-border spinner-border-sm me-1"></span>
              XLSX {{ t('with-image') }}
            </li>
          </ul>
        </div>
      </h3>
      <!-- Loader -->
      <spinner-element :loading="loading" />
      <!-- Komunikat gdy brak danych -->
      <div v-if="noData" class="loading d-flex justify-content-center m-3">
        {{ t('no-data-for-selected-parameters') }}
      </div>
      <!-- Lista wyników -->
      <vehicle-list
        :data-service="vehService"
        :vehicle-list="filtersStore.savedVehicles && filtersStore.savedVehicles.length > 0 ? filtersStore.savedVehicles : vehicles"
        :timer-visible="false"
        details-path="vehicleArch"
        :is-from-tags="isFromTags"
        :show-page-size-selector="true"
        :page-size="pageSize"
        :page-size-options="pageSizeOptions"
        @openDetails="goToDetailsView"
        @page-size-change="setPageSize"
      />
      <!-- Paginacja -->
      <PagePanel
        :page-num="pageNumber"
        :page-size="pageSize"
        :total-size="totalCount"
        v-on:setPage="setPage"
      />
    </div>
  </div>
</template>

<script>
// Importujemy wszystkie zależności, filtry i komponenty
import { useArchivalFiltersStore } from "@/stores/archivalFilters";
import ArchivalMeasurementsService from "../services/ArchivalMeasurementsService";
import LocationList from "./filters/LocationList.vue";
import LocationList2 from "./filters/LocationList2.vue";
import DateRange from "./filters/DateRange.vue";
import LaneNumbers from "./filters/LaneNumbers.vue";
import VehicleCategories from "./filters/VehicleCategories.vue";
import ViolationList from "./filters/ViolationList.vue";
import ArchivalVehicleData from "./archival/ArchivalVehicleData.vue";
import SelectFilter from "./filters/SelectFilter.vue";
import VehicleList from "@/components/VehicleList";
import moment from "moment";
import SpinnerElement from "./SpinnerElement.vue";
import PagePanel from "@/components/table/PagePanel";
import { ref, watch, computed } from 'vue';
import { isSG } from "@/config/appVersion";
import { useRoute, useRouter } from "vue-router";

moment.locale("pl");

export default {
  // Krok procesu przekazywany jako props
  props: {
    step: { type: Number, required: true }
  },
  components: {
    LocationList,
    LocationList2,
    DateRange,
    LaneNumbers,
    VehicleCategories,
    ViolationList,
    ArchivalVehicleData,
    SelectFilter,
    VehicleList,
    SpinnerElement,
    PagePanel,
  },
  name: "ArchivalMeasurements",
  emits: ["nextStep", "previousStep", "clear"],

  // Setup API (composition API)
  setup(props, { emit }) {
    const filtersStore = useArchivalFiltersStore();
    // const step = computed(() => props.step);

    // Stan komponentu
    const vehicles = ref([]);
    const loading = ref(false);
    const noData = ref(false);
    const hideFilters = ref(false);
    const route = useRoute();
    const router = useRouter();

    const vehService = ArchivalMeasurementsService;

    // Stany spinnerów eksportu
    const isDownloadingPdf = ref(false);
    const isDownloadingPdfWithImage = ref(false);
    const isDownloadingCsv = ref(false);
    const isDownloadingCsvWithImage = ref(false);
    const isDownloadingXlsx = ref(false);
    const isDownloadingXlsxWithImage = ref(false);

    // Paginacja
    const pageSizeOptions = [10, 25, 50];
    const getValidPageNumber = (value) => {
      const page = Number(value || 1);
      return Number.isInteger(page) && page > 0 ? page : 1;
    };
    const getValidPageSize = (value) => {
      const size = Number(value || 10);
      return pageSizeOptions.includes(size) ? size : 10;
    };
    const pageNumber = ref(getValidPageNumber(route.query.page));
    const pageSize = ref(getValidPageSize(route.query.limit));
    const totalCount = ref(0);

    // Otwarcie dropdownów eksportu
    const isOpenPdf = ref(false);
    const isOpenCsv = ref(false);
    const isOpenXlsx = ref(false);

    // Czy wyniki są z tagów?
    const isFromTags = ref(false);
    const useMapLocationList = ref(false);
    const locationListComponent = computed(() =>
        useMapLocationList.value ? "LocationList2" : "LocationList"
    );

    const hasAppliedFilters = computed(
      () => Object.keys(filtersStore.appliedFilters || {}).length > 0
    );
    const getFiltersForResults = () =>
      props.step === 4 && hasAppliedFilters.value
        ? filtersStore.appliedFilters
        : filtersStore.filters;

    // reaguj na zmianę query (np. powrót z details)
    watch(
        () => [route.query.page, route.query.limit],
        ([p, limit]) => {
          const newPage = getValidPageNumber(p);
          const newPageSize = getValidPageSize(limit);
          const hasChanged =
              newPage !== pageNumber.value ||
              newPageSize !== pageSize.value;

          if (hasChanged) {
            pageNumber.value = newPage;
            pageSize.value = newPageSize;
            filtersStore.clearSavedResults?.();

            if (props.step === 4) {
              refreshVehicles();
            }
          }
        }
    );

    const refreshVehicles = async () => {
      loading.value = true;
      noData.value = false;

      // console.log('refreshVehicles');

      try {
        let data;
        const filtersForRequest = getFiltersForResults();

        if (!filtersForRequest.taggedOnly) {
          data = await vehService.getAll(pageNumber.value, pageSize.value, filtersForRequest);
          isFromTags.value = false;
        } else {
          data = await vehService.getAllWithTags(filtersForRequest);
          isFromTags.value = true;
        }

        // --- Ujednolicenie odpowiedzi ---
        // 1) TAGI: czasem API zwraca tablicę
        if (Array.isArray(data)) {
          totalCount.value = data.length;
          const totalPages =
              pageSize.value > 0 ? Math.ceil(totalCount.value / pageSize.value) : 0;

          if (totalPages > 0 && pageNumber.value > totalPages) {
            pageNumber.value = totalPages;
            router.replace({
              path: route.path,
              query: {
                ...route.query,
                page: String(pageNumber.value),
                limit: String(pageSize.value),
              },
            });
          }

          const start = (pageNumber.value - 1) * pageSize.value;
          vehicles.value = data.slice(start, start + pageSize.value);
          noData.value = data.length === 0;
          return;
        }

        const items = data?.content ?? data?.items ?? [];
        const total =
            data?.totalCount ??
            data?.totalElements ??   // bardzo częste w paginacji
            data?.total ??           // fallback
            (Array.isArray(items) ? items.length : 0);

        totalCount.value = Number(total) || 0;
        vehicles.value = Array.isArray(items) ? items : [];

        // totalPages (jeśli backend nie zwraca)
        const totalPages =
            data?.totalPages ??
            (pageSize.value > 0 ? Math.ceil(totalCount.value / pageSize.value) : 0);

        // --- Jeśli jesteś poza zakresem stron, cofnij i pobierz ponownie ---
        if (totalPages > 0 && pageNumber.value > totalPages) {
          pageNumber.value = totalPages;

          // sync do URL
          router.replace({
            path: route.path,
            query: {
              ...route.query,
              page: String(pageNumber.value),
              limit: String(pageSize.value),
            },
          });

          // ponów pobranie już dla poprawnej strony
          const data2 = await vehService.getAll(pageNumber.value, pageSize.value, filtersForRequest);

          const items2 = data2?.content ?? data2?.items ?? [];
          const total2 =
              data2?.totalCount ??
              data2?.totalElements ??
              data2?.total ??
              (Array.isArray(items2) ? items2.length : 0);

          totalCount.value = Number(total2) || 0;
          vehicles.value = Array.isArray(items2) ? items2 : [];
        }

        noData.value = totalCount.value === 0;
      } catch (e) {
        filtersStore.setErrors(e);
        vehicles.value = [];
        totalCount.value = 0;
        noData.value = true;
      } finally {
        loading.value = false;
      }
    };

    const setPageSize = (newPageSize) => {
      const size = getValidPageSize(newPageSize);
      if (size === pageSize.value) return;

      pageSize.value = size;
      pageNumber.value = 1;
      hideFilters.value = true;
      filtersStore.clearSavedResults?.();

      router.replace({
        path: route.path,
        query: {
          ...route.query,
          page: "1",
          limit: String(size),
        },
      });

      refreshVehicles();
    };

    const canGoNext = computed(() => {
      if (props.step !== 1) return true;
      const locs = filtersStore.filters?.locations;
      return Array.isArray(locs) && locs.length > 0;
    });

    watch(
        () => props.step,
        (newStep) => {
          // wyjście z wyników → czyść URL
          if (newStep !== 4 && (route.query.page || route.query.limit)) {
            router.replace({ path: route.path, query: {} });
          }

          if (newStep !== 4) {
            hideFilters.value = false;
          }

          // wejście w wyniki
          if (newStep === 4) {
            if (!hasAppliedFilters.value) {
              filtersStore.setAppliedFilters({ ...filtersStore.filters });
            }

            if (filtersStore.savedVehicles?.length > 0) {
              vehicles.value = [...filtersStore.savedVehicles];
              noData.value = vehicles.value.length === 0;
              loading.value = false;
            } else {
              refreshVehicles();
            }
          }
        },
        { immediate: true }
    );

    watch(
        () => filtersStore.shouldReload,
        (val) => {
          if (!val) return;

          filtersStore.setShouldReload(false);

          if (props.step === 4) {
            pageNumber.value = 1;

            router.replace({
              path: route.path,
              query: {
                ...route.query,
                page: "1",
                limit: String(pageSize.value),
              },
            });

            refreshVehicles();
          }
        }
    );

    // Odśwież na wejście w widok wyników
    // watch(() => filtersStore.isResult, (val) => {
    //   if (val) {
    //     refreshVehicles();
    //   }
    // });

    watch(
        () => filtersStore.filters,
        () => {
          hideFilters.value = false;
          vehicles.value = [];
          totalCount.value = 0;
          noData.value = false;
        },
        { deep: true }
    );

    const nextStep = () => {
      if (!canGoNext.value) return;
      emit('nextStep');
    };

    const previousStep = () => {
      emit('previousStep');
    };

    // Wyczyszczenie widoku i reset filtrów
    const clear = () => {
      emit('clear');
      hideFilters.value = false;
    };

    // Zwracamy wszystko co potrzebne w template
    return {
      filtersStore,
      vehicles,
      loading,
      noData,
      hideFilters,
      nextStep,
      previousStep,
      clear,
      refreshVehicles,
      vehService,
      isDownloadingPdf,
      isDownloadingPdfWithImage,
      isDownloadingCsv,
      isDownloadingCsvWithImage,
      isDownloadingXlsx,
      isDownloadingXlsxWithImage,
      pageNumber,
      pageSize,
      pageSizeOptions,
      setPageSize,
      totalCount,
      isOpenPdf,
      isOpenCsv,
      isOpenXlsx,
      isFromTags,
      useMapLocationList,
      locationListComponent,
      isSG,
      canGoNext,
      route,
      router,
      // step,
    };
  },

  // Reszta funkcji (options API)
  methods: {
    // Odśwież dane na podstawie aktualnych filtrów
    refreshData: function () {
      this.hideFilters = true;
      this.refreshVehicles();
    },
    // Otwieranie dropdownów eksportu
    toggleDropdown(type) {
      // if (this.noData) return;
      this.isOpenPdf = type === 'pdf' ? !this.isOpenPdf : false;
      this.isOpenCsv = type === 'csv' ? !this.isOpenCsv : false;
      this.isOpenXlsx = type === 'xlsx' ? !this.isOpenXlsx : false;
    },
    // Obsługa kliknięcia eksportu
    selectOption(type) {
      if (type == 'pdf') {
        this.isDownloadingPdf = true;
      }
      if (type == 'pdf-with-image') {
        this.isDownloadingPdfWithImage = true;
      }
      if (type == 'csv') {
        this.isDownloadingCsv = true;
      }
      if (type == 'csv-with-image') {
        this.isDownloadingCsvWithImage = true;
      }
      if (type == 'xlsx') {
        this.isDownloadingXlsx = true;
      }
      if (type == 'xlsx-with-image') {
        this.isDownloadingXlsxWithImage = true;
      }
      // Pobierz plik w odpowiednim formacie
      if (!this.isFromTags) {
        this.retrieveZip(type);
      } else {
        this.retrieveTagsZip(type);
      }
    },
    // Główna funkcja pobierania archiwalnych pojazdów (nieużywana)
    retrieveArchivalMeasurements() {
      this.loading = true;
      this.refreshData();
    },
    // Ustaw spinner (ładowanie) na podstawie wyników
    setLoading: function () {
      this.loading = this.vehicles.length === 0;
    },
    // Reakcja na zmianę lokalizacji
    runChangedLocations(locations) {
      if (!this.isResult) return;
      if (!locations || locations.length === 0) {
        this.filtersStore.checkRequireData();
        this.clear();
      } else {
        this.refreshVehicles();
      }
    },
    // Reakcja na zmianę filtrów
    runChangedFilters() {
      if (!this.isResult) return;
      this.filtersStore.checkRequireData();
      this.hideFilters = false;
      this.vehicles = [];
    },
    // Zmiana strony w paginacji
    setPage(event) {
      this.pageNumber = event;
      this.filtersStore.clearSavedResults?.();

      // zapis do URL (żeby powrót działał)
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          page: String(event),
          limit: String(this.pageSize),
        },
      });

      this.refreshData();
    },
    // Obsługa spinnerów eksportu
    downloadingIcon(type, value) {
      if (type === "pdf") {
        this.isDownloadingPdf = value;
      }
      if (type === "pdf-with-image") {
        this.isDownloadingPdfWithImage = value;
      }
      if (type === "csv") {
        this.isDownloadingCsv = value;
      }
      if (type === "csv-with-image") {
        this.isDownloadingCsvWithImage = value;
      }
      if (type === "xlsx") {
        this.isDownloadingXlsx = value;
      }
      if (type === "xlsx-with-image") {
        this.isDownloadingXlsxWithImage = value;
      }
    },
    // Pobieranie ZIP z API (standard)
    retrieveZip(type) {
      this.downloadingIcon(type, true);
      this.vehService
        .getZip(type)
        .then((data) => {
          // console.log('archivalmeasurements.vue: data', data);
          this.vehService
            .fetch(data.link)
            .then((response) => {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(response);
              // Nazwa pliku na podstawie daty
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              const hours = String(now.getHours()).padStart(2, '0');
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const fileName = `archive_list_${day}_${month}_${year}_${hours}_${minutes}_${seconds}`;
              link.download = fileName;
              link.click();
              this.downloadingIcon(type, false);
              URL.revokeObjectURL(link.href);
            })
            .catch(console.error)
            .finally(() => {
              this.downloadingIcon(type, false);
              this.closeDropdown(type);
            });
        })
        .catch((error) => {
          this.loading = false;
          this.filtersStore.setErrors(error);
          this.closeDropdown(type);
        });
    },
    // Pobieranie ZIP z API (dla wyników z tagami)
    retrieveTagsZip(type) {
      this.downloadingIcon(type, true);
      this.vehService
        .getTagsZip(type)
        .then((data) => {
          this.vehService
            .fetch(data.link)
            .then((response) => {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(response);
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              const hours = String(now.getHours()).padStart(2, '0');
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const fileName = `archive_list_${day}_${month}_${year}_${hours}_${minutes}_${seconds}`;
              link.download = fileName;
              link.click();
              URL.revokeObjectURL(link.href);
            })
            .catch(console.error)
            .finally(() => {
              this.downloadingIcon(type, false);
              this.closeDropdown(type);
            });
        })
        .catch((error) => {
          this.loading = false;
          this.filtersStore.setErrors(error);
          this.closeDropdown(type);
        });
    },
    // Otwieranie/ukrywanie filtrów po kliknięciu
    clickFilters(event) {
      if (event.target.className !== "collapsed") {
        this.hideFilters = false;
      }
    },
    // Przejście do widoku szczegółowego pojazdu (do routera)
    goToDetailsView(vehicle) {
      this.filtersStore.setSavedState(
        this.filtersStore.filters.locations,
        this.filtersStore.filters,
        this.vehicles
      );
      const path = `/vehicleArch/${vehicle.id}/${vehicle.location.id}/${this.getCreatedAt(vehicle)}`;
      const query = {
        page: String(this.pageNumber),
        limit: String(this.pageSize),
        backTo: this.$route.fullPath,
      };
      this.$router.push({ path, query });
    },
    // Pobierz datę utworzenia pojazdu w ISO
    getCreatedAt(vehicle) {
      return moment(vehicle.createdAt).toISOString(false);
    },
    // Zamknięcie dropdowna eksportu po pobraniu
    closeDropdown(type) {
      if (type === 'pdf' || type === 'pdf-with-image') {
        this.isOpenPdf = false;
      } else if (type === 'csv' || type === 'csv-with-image') {
        this.isOpenCsv = false;
      } else if (type === 'xlsx' || type === 'xlsx-with-image') {
        this.isOpenXlsx = false;
      }
    },
  },
};
</script>

<style scoped>
.dropdown-wrapper {
  position: relative;
  display: inline-block;
}

.dropdown-wrapper .btn {
  margin-top: 5px;
  margin-left: 10px;
  background-color: #e0e0e0;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
}

.dropdown-wrapper .btn:hover {
  background-color: #ccc;
}

.dropdown-wrapper .btn span {
  margin-right: 10px;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  list-style: none;
  padding: 0;
  margin: 0;
  border: 1px solid #ccc;
  width: 180px;
  z-index: 10;
  text-align: center;
}

.dropdown-list li {
  padding: 8px 4px;
  cursor: pointer;
  text-align: center;
  font-size: 14px;
}

.dropdown-list li:hover {
  background: #f0f0f0;
}

.content,
.step-content {
  width: 100%;
  min-height: 478px;
}

.content h2 {
  position: relative;
  /* margin: 0; */
  padding: 15px 20px;
  background-color: white;
  cursor: pointer;
}

.content h2::after {
  width: 22px;
  height: 18px;
  position: absolute;
  left: 100px;
  top: 20px;
  content: "";
  background: url("../assets/icons/filtry.png") no-repeat left top;
}

.step1 {
  width: 100%;
  /* background: #f5f4f2 url("../assets/a-bg.png") repeat-x left top; */
}

.step2 .violations {
  width: 285px;
  border-top: 1px solid #bfbfbf;
}

.step2 .vehicle-categories .category-table {
  background-color: red;
}

h3 {
  position: relative;
  padding: 10px 20px;
  background-color: white;
  border-top: 1px solid #b8b8b6;
  margin-bottom: 0;
}

/* h3 span {
  align-content: center;
  align-items: center;
} */

h3 button {
  position: relative;
  padding: 5px 15px 5px 25px;
  margin-right: 10px;
}

/* h3 button::before { */
  /* position: absolute;
  left: 5px;
  top: 5px;
  content: "";
  width: 30px;
  height: 20px; */
  /* background: url("../assets/icons/pobierz.png") no-repeat left top; */
/* } */

.sort {
  margin-top: 1rem;
}

.actions {
  background-color: #f5f4f2;
}

.actions button {
  border: 0;
  font-size: 19px;
  font-weight: bold;
  background: none;
}

.actions .btn-prev {
  margin-left: 30px;
  padding: 8px 8px 8px 45px;
  position: relative;
  color: #58585a;
}

.actions .btn-prev:before {
  content: "";
  position: absolute;
  left: 0;
  top: 11px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/powrot.png") no-repeat left top;
}

.actions .btn-prev:hover {
  color: black;
}

.actions .btn-prev:hover:before {
  background: url("../assets/icons/powrot_over.png") no-repeat left top;
}

.actions .btn-next {
  margin-right: 50px;
  padding: 8px 40px 8px 8px;
  /* border: 2px solid #58585a; */
  position: relative;
  color: #58585a;
}

.actions .btn-next:before {
  content: "";
  position: absolute;
  right: 0;
  top: 11px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/dalej.png") no-repeat left top;
}

.actions .btn-next:hover {
  color: black;
}

.actions .btn-next:hover:before {
  background: url("../assets/icons/dalej_over.png") no-repeat left top;
}

.btn-top {
  background-color: white;
}

.actions .btn-prev-top {
  margin-left: 10px;
  padding: 8px 8px 8px 45px;
  position: relative;
  color: #58585a;
  background-color: white;
}

.actions .btn-prev-top:before {
  content: "";
  position: absolute;
  left: 0;
  top: 9px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/powrot.png") no-repeat left top;
}

.actions-down {
  background-color: white;
  min-height: 43px;
}

.actions-down button {
  border: 0;
  font-size: 19px;
  font-weight: bold;
  background: none;
}

.location-view-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-left: 20px;
}

.toggle-label {
  font-size: 12px;
  font-weight: 700;
  color: #58585a;
  letter-spacing: 0.04em;
}

.location-view-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background-color: #cfd4da;
  transition: background-color 0.2s ease;
}

.toggle-slider::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease;
}

.location-view-toggle input:checked + .toggle-slider {
  background-color: #35c759;
}

.location-view-toggle input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.actions-down .btn-next {
  margin-right: 50px;
  padding: 8px 40px 8px 8px;
  position: relative;
  color: #58585a;
}

.actions-down .btn-next:before {
  content: "";
  position: absolute;
  right: 0;
  top: 11px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/dalej.png") no-repeat left top;
}

.actions-down .btn-next:hover {
  color: black;
}

.actions-down .btn-next:hover:before {
  background: url("../assets/icons/dalej_over.png") no-repeat left top;
}

.actions-down .btn-prev {
  margin-left: 30px;
  padding: 0 0 0 40px;
  position: relative;
  color: #58585a;
}

.actions-down .btn-prev:before {
  content: "";
  position: absolute;
  left: 0;
  top: 11px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/powrot.png") no-repeat left top;
}

.actions-down .btn-prev:hover {
  color: black;
}

.actions-down .btn-prev:hover:before {
  background: url("../assets/icons/powrot_over.png") no-repeat left top;
}

.total-count {
  margin-right: 170px;
  margin-top: 8px;
  font-size: 17px;
}

/* .filter .name {
  font-size: 20px !important;
} */

/* Panel sortowania – spójny z poprzednim krokiem (mniejsza czcionka, karty) */
.sort-panel {
  --fz: 14px;          /* dopasuj do kroku 2, np. 14px */
  --gap: 12px;
  --pad: 12px;
  --radius: 10px;
  --bd: #e5e7eb;       /* neutral-200 */
  --label: #6b7280;    /* gray-500 */
  font-size: var(--fz);
  display: grid;
  gap: var(--gap);
}

/* Każdy poziom sortowania jako kompaktowa karta */
.sort-panel__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--gap);
  padding: var(--pad);
  border: 1px solid var(--bd);
  border-radius: var(--radius);
  background: #fff;
}

/* Responsywność: na wąskich ekranach pod sobą */
@media (max-width: 900px) {
  .sort-panel__row {
    grid-template-columns: 1fr;
  }
}

/* Ujednolicenie wyglądu wnętrza select-filter (bez modyfikacji komponentu) */
:deep(.sort-panel__control) {
  font-size: inherit;             /* odziedzicza 14px */
}

/* Jeśli w środku jest <label> – zrób je subtelne i zwarte */
:deep(.sort-panel__control label) {
  display: block;
  margin: 0 0 4px 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .01em;
  color: var(--label);
}

/* Jeśli w środku jest <select> lub element robiący za selecta – wyrównaj wysokość */
:deep(.sort-panel__control select),
:deep(.sort-panel__control .select),
:deep(.sort-panel__control .vs__dropdown-toggle),
:deep(.sort-panel__control .v-select .vs__selected-options) {
  min-height: 36px;               /* kompaktowa wysokość */
  line-height: 36px;
  border-radius: 8px;
}

/* Opcjonalnie: usuń nadmierne marginesy wokół kontrolki wewnątrz select-filter */
:deep(.sort-panel__control .field),
:deep(.sort-panel__control .wrapper) {
  margin: 0;
}

.dropdown-wrapper .btn {
  border-radius: 4px;
}
</style>
